import { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useAppointments } from "../../../hook/useAppointments";
import { useDoctors } from "../../../hook/useDoctors";
import AppointmentEditForm, {
  prepareAppointmentForEdit,
  prepareEditDataForApi,
} from "./AppointmentEditForm";
import AppointmentTable from "./AppointmentTable";
import PageHeading from "../../PageHeading";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import AppointmentFiltersAndSummary from "../../AppointmentFiltersAndSummary";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const AppointmentList = () => {
  const {
    appointments,
    fetchAppointments,
    updateAppointment,
    approveAppointment,
  } = useAppointments();
  const { doctors, fetchDoctors } = useDoctors();
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [patientSearch, setPatientSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [editMode, setEditMode] = useState(null);
  const [editData, setEditData] = useState({});
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [approvalTargetId, setApprovalTargetId] = useState(null);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteIds, setBulkDeleteIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [appointmentsPerPage] = useState(10);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewAppointment, setViewAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const dropdownTriggerRefs = useRef({});

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Appointments"},
    { label: "Appointment List" },
  ];

  // Doctor options for DynamicSelect
  const doctorOptions = doctors.map((doc) => ({
    value: doc.id,
    label: doc.name || doc.translations?.en?.name || "Doctor",
  }));

  // Fetch data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchAppointments(), fetchDoctors()]);
        // console.log("Fetched Appointments:", appointments);
        // console.log("Fetched Doctors:", doctors);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load appointments or doctors");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchAppointments, fetchDoctors]);

  // Apply filters
  useEffect(() => {
    let result = [...appointments];
    if (filterStatus === "complete") {
      result = result.filter((app) => app.status === "approved");
    } else if (filterStatus === "pending") {
      result = result.filter((app) => app.status === "pending");
    } else if (filterStatus === "cancelled") {
      result = result.filter((app) => app.status === "cancelled");
    }
    if (selectedDoctor) {
      result = result.filter((app) => app.doctorId === selectedDoctor);
    }
    if (patientSearch.trim()) {
      const search = patientSearch.toLowerCase();
      result = result.filter(
        (app) =>
          (app.patientName && app.patientName.toLowerCase().includes(search)) ||
          (app.mobileNumber && app.mobileNumber.includes(search))
      );
    }
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      result = result.filter(
        (app) => new Date(app.appointmentDate || app.createdAt) >= start
      );
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter(
        (app) => new Date(app.appointmentDate || app.createdAt) <= end
      );
    }
    setFilteredAppointments(result);
    setCurrentPage(1);
    // console.log("Filter Inputs:", { filterStatus, selectedDoctor, patientSearch, startDate, endDate });
    console.log("Filtered Appointments:", result);
  }, [appointments, filterStatus, selectedDoctor, patientSearch, startDate, endDate]);

  // Handlers
  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const handleView = (appointment) => {
    setViewAppointment(appointment);
    setViewModalOpen(true);
    setActiveDropdown(null);
  };

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleEditStart = (appointment) => {
    setEditMode(appointment.id);
    setEditData(prepareAppointmentForEdit(appointment));
  };

  const handleSaveEdit = async (id) => {
    try {
      const payload = prepareEditDataForApi(editData);
      await updateAppointment(id, payload);
      toast.success("Appointment updated successfully!");
      setEditMode(null);
      fetchAppointments();
    } catch (err) {
      toast.error("Update failed.");
    }
  };

  const handleApproveClick = (id) => {
    setApprovalTargetId(id);
    setApprovalModalVisible(true);
  };

  const handleCancelClick = (id) => {
    setCancelTargetId(id);
    setCancelModalVisible(true);
  };

  const confirmApproval = async (id) => {
    try {
      await approveAppointment(id);
      toast.success("Appointment approved successfully!");
      fetchAppointments();
    } catch (error) {
      toast.error("Failed to approve appointment");
    }
  };

  const confirmCancellation = async (id) => {
    try {
      await axios.put(
        `${BASE_URL}/appointment/cancel/${id}`,
        {},
        {
          headers: { "x-api-key": API_KEY },
        }
      );
      toast.success("Appointment cancelled successfully!");
      fetchAppointments();
    } catch (error) {
      toast.error("Failed to cancel appointment");
    }
  };

  const confirmBulkDelete = async (ids) => {
    try {
      await Promise.all(
        ids.map((id) =>
          axios.delete(`${BASE_URL}/appointment/delete/${id}`, {
            headers: { "x-api-key": API_KEY },
          })
        )
      );
      toast.success(`Appointment${ids.length > 1 ? "s" : ""} deleted successfully!`);
      fetchAppointments();
      setBulkDeleteIds([]);
    } catch (error) {
      toast.error("Bulk deletion failed.");
    }
  };

  const getStatusLabel = (appointment) => {
    if (appointment.status === "cancelled") return "Cancelled";
    if (appointment.status === "approved") return "Complete";
    return "Pending";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString("en-US", { timeZone: "UTC" });
  };

  const handleReset = () => {
    setFilterStatus("all");
    setSelectedDoctor("");
    setPatientSearch("");
    setStartDate("");
    setEndDate("");
    setFilteredAppointments(appointments);
    setCurrentPage(1);
  };

  return (
    <div>
      <ToastContainer />
      <PageHeading title="Appointment List" breadcrumbs={breadcrumbs} />

      <>
        {/* Reusable Filters and Summary */}
        <AppointmentFiltersAndSummary
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          selectedDoctor={selectedDoctor}
          setSelectedDoctor={setSelectedDoctor}
          patientSearch={patientSearch}
          setPatientSearch={setPatientSearch}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          appointments={filteredAppointments}
          doctorOptions={doctorOptions}
          onReset={handleReset}
          showPatientSearch={true}
          showDateFilters={true}
          totalLabel="Total Appointments"
        />

        {/* Edit Mode */}
        {editMode && (
          <AppointmentEditForm
            editData={editData}
            handleEditChange={handleEditChange}
            handleSaveEdit={handleSaveEdit}
            editMode={editMode}
            onCancel={() => setEditMode(null)}
          />
        )}

        {/* View Modal */}
        {viewModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-full sm:max-w-[80%] md:max-w-[700px] lg:max-w-screen-lg max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg sm:text-xl font-bold text-gray-700">Appointment Details</h2>
              </div>
              <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {viewAppointment && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ID</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.id}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Patient</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.patientName || "N/A"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Doctor</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.doctorName || "Unknown"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {formatDate(viewAppointment.appointmentDate || viewAppointment.createdAt)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Serial</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.serialNumber || "--"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.mobileNumber || "N/A"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {getStatusLabel(viewAppointment)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Weight</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.weight || "N/A"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.age || "N/A"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.bloodGroup || "N/A"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Consultation Fee</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.consultationFee || "N/A"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.paymentMethod || "N/A"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.reason || "N/A"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                        {viewAppointment.address || "N/A"}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setViewModalOpen(false)}
                  className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <AppointmentTable
          data={filteredAppointments}
          currentPage={currentPage}
          itemsPerPage={appointmentsPerPage}
          onPageChange={setCurrentPage}
          onEdit={handleEditStart}
          onDelete={(ids) => {
            setBulkDeleteIds(Array.isArray(ids) ? ids : [ids]);
            setShowBulkDeleteModal(true);
          }}

          handleApproveClick={handleApproveClick}
          handleCancelClick={handleCancelClick}
          getStatusLabel={getStatusLabel}
          formatDate={formatDate}
          toggleDropdown={toggleDropdown}
          activeDropdown={activeDropdown}
          dropdownTriggerRefs={dropdownTriggerRefs}
          handleView={handleView}
          setActiveDropdown={setActiveDropdown}
          isLoading={loading}
        />

        {/* Modals */}
        <ConfirmActionModal
          isOpen={approvalModalVisible}
          onClose={() => {
            setApprovalModalVisible(false);
            setApprovalTargetId(null);
          }}
          onConfirm={() => confirmApproval(approvalTargetId)}
          ids={approvalTargetId}
          entityName="appointment"
          title="Confirm Approval"
          message="Are you sure you want to approve this appointment?"
          confirmText="Approve"
          cancelText="Cancel"
        />
        <ConfirmActionModal
          isOpen={cancelModalVisible}
          onClose={() => {
            setCancelModalVisible(false);
            setCancelTargetId(null);
          }}
          onConfirm={() => confirmCancellation(cancelTargetId)}
          ids={cancelTargetId}
          entityName="appointment"
          title="Confirm Cancellation"
          message="Are you sure you want to cancel this appointment?"
          confirmText="Yes, Cancel"
          cancelText="No"
        />
        <ConfirmActionModal
          isOpen={showBulkDeleteModal}
          onClose={() => {
            setShowBulkDeleteModal(false);
            setBulkDeleteIds([]);
          }}
          onConfirm={() => confirmBulkDelete(bulkDeleteIds)}
          ids={bulkDeleteIds}
          entityName="appointment"
          title="Confirm Delete"
          message={
            Array.isArray(bulkDeleteIds) && bulkDeleteIds.length > 1
              ? `Are you sure you want to delete ${bulkDeleteIds.length} appointments?`
              : "Are you sure you want to delete this appointment?"
          }
          confirmText="Delete"
          cancelText="Cancel"
        />
      </>
    </div>
  );
};

export default AppointmentList;